/**
 * LeaderboardPreview
 * Compact reusable widget showing top 3 of each category — used on the
 * Customer Dashboard and the Rewards page. Links to the full /leaderboard page.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { Trophy, Crown, Users, Sparkles, Calendar, ArrowRight } from 'lucide-react';

const CATEGORIES = [
  { key: 'top_visits_this_month', label: 'Top 5 This Month', icon: Calendar, color: 'text-lime-400' },
  { key: 'most_loyal',            label: 'Most Loyal',       icon: Crown,    color: 'text-yellow-400' },
  { key: 'most_referrals',        label: 'Most Referrals',   icon: Users,    color: 'text-cyan-400' },
  { key: 'most_points',           label: 'Most Points',      icon: Sparkles, color: 'text-purple-300' },
];

const LeaderboardPreview = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/leaderboard');
        setData(res.data);
      } catch (e) {
        console.debug('leaderboard fetch', e?.response?.status);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: 'var(--ss-white-soft, #aaa)' }}>
        Loading leaderboard…
      </div>
    );
  }
  if (!data) return null;

  const limit = compact ? 3 : 5;

  return (
    <div data-testid="leaderboard-preview" style={{
      background: 'linear-gradient(160deg, rgba(40,0,60,0.45), rgba(15,15,15,0.55))',
      border: '1px solid rgba(170,255,0,0.25)',
      borderRadius: 18, padding: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Trophy className="w-6 h-6" style={{ color: 'gold' }} />
          <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#fff' }}>
            Leaderboard — {data.month}
          </h3>
        </div>
        <Link
          to="/leaderboard"
          style={{
            color: 'var(--ss-green, #aaff00)', fontSize: '0.82rem',
            display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none',
          }}
          data-testid="leaderboard-view-all-link"
        >
          View all <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* Winner of the month banner */}
      {data.winner_of_month && (
        <div data-testid="leaderboard-winner-banner" style={{
          padding: '12px 14px',
          marginBottom: 14,
          borderRadius: 12,
          background: 'linear-gradient(120deg, rgba(255,215,0,0.18), rgba(170,0,255,0.18))',
          border: '1px solid rgba(255,215,0,0.45)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <Trophy className="w-7 h-7" style={{ color: 'gold' }} />
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 800, color: '#fff' }}>
              🏆 {data.winner_of_month.name} — Winner of {data.month}
            </p>
            {data.winner_of_month.prize && (
              <p style={{ margin: '2px 0 0 0', fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                Prize: {data.winner_of_month.prize}
              </p>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr 1fr' : '1fr 1fr', gap: 12 }}>
        {CATEGORIES.map((cat) => {
          const rows = (data[cat.key] || []).slice(0, limit);
          const Icon = cat.icon;
          return (
            <div key={cat.key} data-testid={`lb-category-${cat.key}`} style={{
              background: 'rgba(0,0,0,0.35)', borderRadius: 12, padding: 12,
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <Icon className={`w-4 h-4 ${cat.color}`} />
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {cat.label}
                </span>
              </div>
              {rows.length === 0 ? (
                <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }}>
                  No data yet — be the first 👀
                </p>
              ) : (
                rows.map((r, idx) => (
                  <div key={r.user_id || idx} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 0',
                    borderBottom: idx < rows.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 999,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.72rem', fontWeight: 800,
                      background: idx === 0 ? 'rgba(255,215,0,0.25)' :
                                  idx === 1 ? 'rgba(192,192,192,0.25)' :
                                  idx === 2 ? 'rgba(205,127,50,0.25)' : 'rgba(255,255,255,0.06)',
                      color: idx === 0 ? 'gold' :
                             idx === 1 ? '#dcdcdc' :
                             idx === 2 ? '#cd7f32' : 'rgba(255,255,255,0.6)',
                    }}>
                      {idx + 1}
                    </span>
                    <span style={{ flex: 1, fontSize: '0.85rem', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.name}
                    </span>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ss-green, #aaff00)' }}>
                      {r.value.toLocaleString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaderboardPreview;
